import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  RHFCivilityField,
  RHFCountrySelect,
  RHFTextField,
} from 'components/RHF';
import { SubscribeFormValues } from 'interfaces/subscribe.types';
import { POSTAL_CODE_REGEX } from '../../../constants';
import {
  AddressSectionContainer,
  InputsInline,
  PostalCodeCityInline,
} from '../SubscribeForm.styles';

type AddressSectionProps = {
  shouldShow: (apiName: string, fallback?: boolean) => boolean;
  isRequired: (apiName: string, fallback?: boolean) => boolean;
  isDisabled: (apiName: string, fallback?: boolean) => boolean;
  label: (apiName: string, fallbackKey?: string) => string;
  renderTooltip: (apiName: string) => JSX.Element | undefined;
};

const AddressSection = ({
  shouldShow,
  isRequired,
  isDisabled,
  label,
  renderTooltip,
}: AddressSectionProps): JSX.Element => {
  const { t } = useTranslation();
  const { control, errors } = useFormContext<SubscribeFormValues>();

  return (
    <AddressSectionContainer>
      <legend>{t('auth.signup.address.sectionTitle')}</legend>

      {shouldShow('addressTitle', false) && (
        <RHFTextField
          name="addressName"
          control={control}
          label={label('addressTitle', 'auth.signup.address.name')}
          placeholder={t('auth.signup.address.name_placeholder')}
          required={isRequired('addressTitle', false)}
          disabled={isDisabled('addressTitle')}
          error={errors.addressName}
          rules={
            isRequired('addressTitle', false) &&
            !isDisabled('addressTitle')
              ? { required: t('core.required') as string }
              : {}
          }
          addons={renderTooltip('addressTitle')}
        />
      )}

      {shouldShow('addressGender', false) && (
        <RHFCivilityField
          name="addressGender"
          control={control}
          label={label('addressGender', 'auth.signup.address.civility')}
          required={isRequired('addressGender', false)}
          disabled={isDisabled('addressGender')}
          error={errors.addressGender}
          tooltip={renderTooltip('addressGender')}
        />
      )}

      <InputsInline>
        {shouldShow('addressLastName', false) && (
          <RHFTextField
            name="addressLastName"
            control={control}
            label={label('addressLastName', 'auth.signup.address.lastName')}
            placeholder={t('auth.signup.address.lastName_placeholder')}
            required={isRequired('addressLastName', false)}
            disabled={isDisabled('addressLastName')}
            error={errors.addressLastName}
            rules={
              isRequired('addressLastName', false) && !isDisabled('addressLastName')
                ? { required: t('core.required') as string }
                : {}
            }
            addons={renderTooltip('addressLastName')}
            autoComplete="family-name"
          />
        )}
        {shouldShow('addressFirstName', false) && (
          <RHFTextField
            name="addressFirstName"
            control={control}
            label={label('addressFirstName', 'auth.signup.address.firstName')}
            placeholder={t('auth.signup.address.firstName_placeholder')}
            required={isRequired('addressFirstName', false)}
            disabled={isDisabled('addressFirstName')}
            error={errors.addressFirstName}
            rules={
              isRequired('addressFirstName', false) &&
              !isDisabled('addressFirstName')
                ? { required: t('core.required') as string }
                : {}
            }
            addons={renderTooltip('addressFirstName')}
            autoComplete="given-name"
          />
        )}
      </InputsInline>

      {shouldShow('addressCompanyName', false) && (
        <RHFTextField
          name="addressCompanyName"
          control={control}
          label={label(
            'addressCompanyName',
            'auth.signup.address.companyName',
          )}
          placeholder={t('auth.signup.address.companyName_placeholder')}
          required={isRequired('addressCompanyName', false)}
          disabled={isDisabled('addressCompanyName')}
          error={errors.addressCompanyName}
          rules={
            isRequired('addressCompanyName', false) &&
            !isDisabled('addressCompanyName')
              ? { required: t('core.required') as string }
              : {}
          }
          addons={renderTooltip('addressCompanyName')}
          autoComplete="organization"
        />
      )}

      {shouldShow('addressLine1', false) && (
        <RHFTextField
          name="addressStreet"
          control={control}
          label={label('addressLine1', 'auth.signup.address.street')}
          placeholder={t('auth.signup.address.street_placeholder')}
          required={isRequired('addressLine1', false)}
          disabled={isDisabled('addressLine1')}
          error={errors.addressStreet}
          rules={
            isRequired('addressLine1', false) &&
            !isDisabled('addressLine1')
              ? { required: t('core.required') as string }
              : {}
          }
          addons={renderTooltip('addressLine1')}
          autoComplete="street-address"
        />
      )}

      {shouldShow('addressLine2', false) && (
        <RHFTextField
          name="addressComplement"
          control={control}
          label={label('addressLine2', 'auth.signup.address.complement')}
          placeholder={t('auth.signup.address.complement_placeholder')}
          required={isRequired('addressLine2', false)}
          disabled={isDisabled('addressLine2')}
          error={errors.addressComplement}
          rules={
            isRequired('addressLine2', false) &&
            !isDisabled('addressLine2')
              ? { required: t('core.required') as string }
              : {}
          }
          addons={renderTooltip('addressLine2')}
          autoComplete="address-line2"
        />
      )}

      <PostalCodeCityInline>
        {shouldShow('addressZipCode', false) && (
          <RHFTextField
            name="addressPostalCode"
            control={control}
            label={label(
              'addressZipCode',
              'auth.signup.address.postalCode',
            )}
            placeholder={t('auth.signup.address.postalCode_placeholder')}
            required={isRequired('addressZipCode', false)}
            disabled={isDisabled('addressZipCode')}
            error={errors.addressPostalCode}
            rules={{
              ...(isRequired('addressZipCode', false) &&
              !isDisabled('addressZipCode')
                ? { required: t('core.required') as string }
                : {}),
              ...(!isDisabled('addressZipCode')
                ? {
                    pattern: {
                      value: POSTAL_CODE_REGEX,
                      message: t('core.invalid_postal_code') as string,
                    },
                  }
                : {}),
            }}
            addons={renderTooltip('addressZipCode')}
            autoComplete="postal-code"
            inputMode="numeric"
          />
        )}
        {shouldShow('addressCity', false) && (
          <RHFTextField
            name="addressCity"
            control={control}
            label={label('addressCity', 'auth.signup.address.city')}
            placeholder={t('auth.signup.address.city_placeholder')}
            required={isRequired('addressCity', false)}
            disabled={isDisabled('addressCity')}
            error={errors.addressCity}
            rules={
              isRequired('addressCity', false) && !isDisabled('addressCity')
                ? { required: t('core.required') as string }
                : {}
            }
            addons={renderTooltip('addressCity')}
            autoComplete="address-level2"
          />
        )}
      </PostalCodeCityInline>

      {shouldShow('addressCountry', false) && (
        <RHFCountrySelect
          name="addressCountry"
          control={control}
          label={label('addressCountry', 'auth.signup.address.country')}
          placeholder={t('auth.signup.address.country_placeholder')}
          required={isRequired('addressCountry', false)}
          disabled={isDisabled('addressCountry')}
          error={errors.addressCountry}
          rules={
            isRequired('addressCountry', false) && !isDisabled('addressCountry')
              ? { required: t('core.required') as string }
              : {}
          }
          addons={renderTooltip('addressCountry')}
        />
      )}
    </AddressSectionContainer>
  );
};

export default AddressSection;
